import { Box, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Navigate, redirect, useNavigate } from '@tanstack/react-router';
import { IUser, QueryKey } from 'common-types';
import { RouteErrorBoundary } from 'components/route-errors';
import { ContentLoader } from 'components/route-loaders';
import { endOfToday, getUnixTime } from 'date-fns';
import { useGetUserData } from 'hooks/use-get-user-data';
import { useSetUserData } from 'hooks/use-set-user-data';
import type { JSX } from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { dayParamSchema } from 'router/schemas';

const StylePlanDay = lazy(() => import('pages/style-plan-day'));

const Loader = (): JSX.Element => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

export const Route = createFileRoute('/_authenticated/_main/style-plan/$day')({
  beforeLoad: ({ params }) => {
    const result = dayParamSchema.safeParse(params);
    if (!result.success) {
      throw redirect({ to: '/style-plan' });
    }
    return { validatedDay: result.data.day };
  },
  pendingComponent: () => <ContentLoader minHeight="50vh" />,
  errorComponent: RouteErrorBoundary,
  component: StylePlanDayWrapper,
});

function StylePlanDayWrapper(): JSX.Element {
  const { day } = Route.useParams();
  const { isSubscriptionExpired, userProgress, isSuccess } = useGetUserData();
  const { mutateAsync } = useSetUserData();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const requestedDay = parseInt(day, 10);
  const currentDay = userProgress?.currentDay || 1;
  const isAdvanceCase =
    requestedDay === currentDay + 1 && (userProgress?.isDayCompleted || userProgress?.isDayComplete);

  useEffect(() => {
    if (!isSuccess) return;

    // No progress → start at day 1
    if (!userProgress || !userProgress?.currentDay) {
      void navigate({ to: '/style-plan/1' });
      return;
    }

    // Day = currentDay + 1 AND day completed → advance progress, allow
    if (isAdvanceCase) {
      const newCurrentDay = currentDay + 1;

      void mutateAsync({
        userProgress: {
          currentDay: newCurrentDay,
          currentPart: 0,
          timestamp: getUnixTime(endOfToday()),
          isDayCompleted: false,
          isDayComplete: false,
        },
      });

      queryClient.setQueryData([QueryKey.USER_DATA], (prev: IUser) => ({
        ...prev,
        userProgress: {
          currentDay: newCurrentDay,
          currentPart: 0,
          timestamp: getUnixTime(endOfToday()),
          isDayCompleted: false,
          isDayComplete: false,
        },
      }));

      void queryClient.invalidateQueries({ queryKey: [QueryKey.USER_DATA, 'progress'] });
      void navigate({
        to: '/style-plan/$day',
        // @ts-expect-error strictNullChecks breaks TanStack Router inference
        params: { day: String(newCurrentDay) },
        replace: true,
      });
      return;
    }

    // Day beyond progress → redirect to current day
    if (requestedDay > currentDay) {
      void navigate({
        to: '/style-plan/$day',
        // @ts-expect-error strictNullChecks breaks TanStack Router inference
        params: { day: String(currentDay) },
        replace: true,
      });
      return;
    }

    // Day = currentDay → normalize URL
    if (requestedDay === currentDay) {
      void navigate({
        to: '/style-plan/$day',
        // @ts-expect-error strictNullChecks breaks TanStack Router inference
        params: { day: String(currentDay) },
        replace: true,
      });
    }
  }, [day, isSuccess, navigate, userProgress, currentDay]);

  if (isSubscriptionExpired) {
    return <Navigate to="/renew-subscription" />;
  }

  // Show loader while data loads or while redirecting (except advance case)
  if (!isSuccess || (!isAdvanceCase && requestedDay > currentDay)) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <StylePlanDay />
    </Suspense>
  );
}
